import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  Link as LinkIcon, Copy, ExternalLink, MoreHorizontal, Plus, Search, Filter, Trash2,
  Pause, Play, BarChart3, QrCode, Lock, Clock, Hash, Download, Edit,
  Grid3X3, List, ArrowUpDown, Activity, MousePointerClick, Users
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EmptyState } from '@/components/data-display';
import { toast } from 'sonner';
import { apiV1 } from '@/api/client';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const PER_PAGE = 50;

const SORT_OPTIONS = [
  { value: 'name_asc', label: 'Title (A-Z)' },
  { value: 'name_desc', label: 'Title (Z-A)' },
  { value: 'clicks_desc', label: 'Most Clicks' },
  { value: 'clicks_asc', label: 'Fewest Clicks' },
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
];

const SORT_MAP = {
  newest:      ['created_at', 'desc'],
  oldest:      ['created_at', 'asc'],
  name_asc:    ['title', 'asc'],
  name_desc:   ['title', 'desc'],
  clicks_desc: ['total_clicks', 'desc'],
  clicks_asc:  ['total_clicks', 'asc'],
};

const VIEW_MODES = [
  { value: 'table', icon: List },
  { value: 'grid', icon: Grid3X3 },
];

export default function ShortURLs() {
  const navigate = useNavigate();
  const [urls, setUrls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, page: 1, per_page: PER_PAGE, has_more: false });
  const [page, setPage] = useState(1);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 300);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('table');
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchQuery, statusFilter, activeTab, sortBy]);

  const fetchUrls = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('per_page', String(PER_PAGE));
      if (debouncedSearchQuery.trim()) params.append('search', debouncedSearchQuery.trim());
      const effectiveStatus = activeTab !== 'all' ? activeTab : (statusFilter !== 'all' ? statusFilter : null);
      if (effectiveStatus) params.append('status', effectiveStatus);
      const [sortField, sortDir] = SORT_MAP[sortBy] || SORT_MAP.newest;
      params.append('sort_by', sortField);
      params.append('sort_order', sortDir);

      const response = await apiV1.get(`/urls/?${params.toString()}`);
      const data = response.data;

      if (data.success) {
        setUrls(data.data?.items || data.data || []);
        setPagination(data.data?.pagination || data.pagination || { total: (data.data || []).length, page, per_page: PER_PAGE, has_more: false });
      }
    } catch (error) {
      console.error('Error fetching URLs:', error);
      toast.error('Failed to load short URLs');
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearchQuery, statusFilter, activeTab, sortBy]);

  useEffect(() => {
    fetchUrls();
  }, [fetchUrls]);

  // Search, status, and sort are handled server-side. No client-side filtering needed.
  const filteredUrls = urls;

  // Stats come from the server: the list above is one page, so counting it
  // would cap every card at PER_PAGE.
  const [stats, setStats] = useState({ total: 0, activeCount: 0, pausedCount: 0, totalClicks: 0, uniqueClicks: 0, customSlugs: 0 });

  const fetchStats = useCallback(async () => {
    try {
      const response = await apiV1.get('/urls/stats');
      if (response.data?.success) {
        const d = response.data.data || {};
        setStats({
          total: d.total || 0,
          activeCount: d.active || 0,
          pausedCount: d.paused || 0,
          totalClicks: d.total_clicks || 0,
          uniqueClicks: d.unique_clicks || 0,
          customSlugs: d.custom_slugs || 0,
        });
      }
    } catch (error) {
      console.error('Error fetching URL stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await apiV1.put(`/urls/${id}`, { status: newStatus });
      toast.success(`URL ${newStatus === 'active' ? 'activated' : 'paused'}`);
      fetchUrls();
      fetchStats();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to archive this URL?')) return;
    
    try {
      await apiV1.delete(`/urls/${id}`);
      toast.success('URL archived');
      fetchUrls();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete URL');
    }
  };

  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      const response = await apiV1.get(`/urls/export/csv?${params.toString()}`, {
        responseType: 'blob'
      });
      const blob = response.data;
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `short_urls_export_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  const getStatusBadge = (url) => {
    if (url.is_expired) {
      return <Badge variant="destructive">Expired</Badge>;
    }
    if (url.is_click_limited) {
      return <Badge variant="secondary">Limit Reached</Badge>;
    }
    
    const config = {
      active: { className: 'bg-green-500/10 text-green-600 border-green-200' },
      paused: { className: 'bg-amber-500/10 text-amber-600 border-amber-200' },
      archived: { className: 'bg-gray-500/10 text-gray-600 border-gray-200' }
    };
    
    const { className } = config[url.status] || config.active;
    return (
      <Badge variant="outline" className={className}>
        {url.status.charAt(0).toUpperCase() + url.status.slice(1)}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6" data-testid="short-urls-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Short URLs</h1>
          <p className="text-muted-foreground">Create and manage shortened URLs with tracking</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Export CSV */}
          <Button variant="outline" onClick={handleExportCSV} data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>

          <Link to="/urls/new">
            <Button data-testid="create-url-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Short URL
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-primary/10 to-primary/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <LinkIcon className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-muted-foreground">Total URLs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/10 rounded-lg">
                <Activity className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.activeCount}</p>
                <p className="text-xs text-muted-foreground">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <MousePointerClick className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalClicks.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Total Clicks</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/10 rounded-lg">
                <Hash className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.customSlugs}</p>
                <p className="text-xs text-muted-foreground">Custom Slugs</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Status */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="all" className="flex items-center gap-2">
            <LinkIcon className="w-4 h-4" />
            All ({stats.total})
          </TabsTrigger>
          <TabsTrigger value="active" className="flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Active ({stats.activeCount})
          </TabsTrigger>
          <TabsTrigger value="paused" className="flex items-center gap-2">
            <Pause className="w-4 h-4" />
            Paused ({stats.pausedCount})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Search and Filters Bar */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by title, slug, destination, agent..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="search-input"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[150px]" data-testid="sort-select">
              <ArrowUpDown className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex border rounded-md">
            {VIEW_MODES.map(mode => (
              <Button
                key={mode.value}
                variant={viewMode === mode.value ? 'secondary' : 'ghost'}
                size="icon"
                onClick={() => setViewMode(mode.value)}
                className="rounded-none first:rounded-l-md last:rounded-r-md"
                data-testid={`view-${mode.value}`}
              >
                <mode.icon className="w-4 h-4" />
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Results count */}
      {searchQuery && (
        <p className="text-sm text-muted-foreground">
          Found {pagination.total ?? filteredUrls.length} result{(pagination.total ?? filteredUrls.length) !== 1 ? 's' : ''} for "{searchQuery}"
        </p>
      )}

      {/* Grid or Table View */}
      {loading ? (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Short URL</TableHead>
                  <TableHead>Clicks</TableHead>
                  <TableHead>Tracking</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-4 w-[200px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[180px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[60px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[100px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[70px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[80px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[30px]" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : filteredUrls.length === 0 ? (
        // Empty-state copy reads slightly differently when we're filtering
        // vs genuinely empty — the CTA only appears in the truly-empty case
        // because "Create a short URL" doesn't fix "no results match your filter".
        <EmptyState
          icon={LinkIcon}
          title={searchQuery || activeTab !== 'all' ? 'No matching URLs' : 'No short URLs yet'}
          description={searchQuery || activeTab !== 'all'
            ? 'Try adjusting your search or filters.'
            : 'Short URLs are short, brandable redirects you can drop into emails, SMS, or social posts. Create your first one to start tracking clicks.'}
          actionLabel={!searchQuery && activeTab === 'all' ? 'Create Short URL' : undefined}
          onAction={!searchQuery && activeTab === 'all' ? () => navigate('/urls/new') : undefined}
        />
      ) : viewMode === 'grid' ? (
        /* Grid View */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="urls-grid">
          {filteredUrls.map((url) => (
            <Card key={url.id} className="hover:shadow-md transition-shadow" data-testid={`url-card-${url.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {url.is_custom_slug && <Hash className="w-4 h-4 text-primary" title="Custom slug" />}
                    {url.password_hash && <Lock className="w-4 h-4 text-amber-500" title="Password protected" />}
                    {url.expires_at && <Clock className="w-4 h-4 text-blue-500" title="Has expiration" />}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => copyToClipboard(url.short_url)}>
                        <Copy className="w-4 h-4 mr-2" />
                        Copy URL
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <a href={url.short_url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Open URL
                        </a>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to={`/urls/${url.id}?edit=1`}>
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(url.id)}>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Archive
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <h3 className="font-medium truncate mb-1" title={url.title}>{url.title}</h3>
                <code className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded truncate block mb-3">
                  {url.short_url?.replace('https://', '')}
                </code>
                <div className="flex items-center justify-between">
                  {getStatusBadge(url)}
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <MousePointerClick className="w-4 h-4" />
                    {(url.total_clicks || 0).toLocaleString()}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        /* Table View */
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Short URL</TableHead>
                  <TableHead>Clicks</TableHead>
                  <TableHead>Tracking</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUrls.map((url) => (
                  <TableRow key={url.id} data-testid={`url-row-${url.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {url.is_custom_slug && (
                          <Hash className="w-3 h-3 text-primary" title="Custom slug" />
                        )}
                        {url.password_hash && (
                          <Lock className="w-3 h-3 text-amber-500" title="Password protected" />
                        )}
                        {url.expires_at && (
                          <Clock className="w-3 h-3 text-blue-500" title="Has expiration" />
                        )}
                        <span className="font-medium truncate max-w-[200px]" title={url.title}>
                          {url.title}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <code className="text-sm bg-muted px-2 py-0.5 rounded truncate max-w-[180px]">
                          {url.short_url?.replace('https://', '')}
                        </code>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => copyToClipboard(url.short_url)}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Link to={`/urls/analytics/${url.id}`} className="flex items-center gap-2 hover:text-primary transition-colors">
                        <div className="text-sm">
                          <span className="font-medium">{url.total_clicks?.toLocaleString() || 0}</span>
                          <span className="text-muted-foreground"> / {url.unique_clicks?.toLocaleString() || 0} unique</span>
                        </div>
                        <BarChart3 className="w-4 h-4 text-muted-foreground hover:text-primary" />
                      </Link>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {url.tracking?.region_name && (
                          <Badge variant="outline" className="text-xs">{url.tracking.region_name}</Badge>
                        )}
                        {url.tracking?.agent_name && (
                          <Badge variant="outline" className="text-xs">{url.tracking.agent_name}</Badge>
                        )}
                        {url.tracking?.listing_ref && (
                          <Badge variant="secondary" className="text-xs">{url.tracking.listing_ref}</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(url)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(url.created_at)}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => copyToClipboard(url.short_url)}>
                            <Copy className="w-4 h-4 mr-2" />
                            Copy URL
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <a href={url.short_url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="w-4 h-4 mr-2" />
                              Open URL
                            </a>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem asChild>
                            <Link to={`/urls/analytics/${url.id}`}>
                              <BarChart3 className="w-4 h-4 mr-2" />
                              View Analytics
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link to={`/urls/${url.id}?edit=1`}>
                              <Edit className="w-4 h-4 mr-2" />
                              Edit URL
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {url.status === 'active' ? (
                            <DropdownMenuItem onClick={() => handleStatusChange(url.id, 'paused')}>
                              <Pause className="w-4 h-4 mr-2" />
                              Pause
                            </DropdownMenuItem>
                          ) : url.status === 'paused' && (
                            <DropdownMenuItem onClick={() => handleStatusChange(url.id, 'active')}>
                              <Play className="w-4 h-4 mr-2" />
                              Activate
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem 
                            onClick={() => handleDelete(url.id)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Archive
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {(pagination.total > 0 || urls.length > 0) && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground" data-testid="pagination-summary">
            Page {pagination.page || page} of {Math.max(1, Math.ceil((pagination.total || 0) / (pagination.per_page || PER_PAGE)))} — {pagination.total || urls.length} total
          </p>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  data-testid="pagination-prev"
                  aria-disabled={page <= 1}
                  onClick={(e) => {
                    e.preventDefault();
                    if (page > 1) setPage(p => p - 1);
                  }}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink href="#" isActive onClick={(e) => e.preventDefault()}>
                  {pagination.page || page}
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  data-testid="pagination-next"
                  aria-disabled={!pagination.has_more}
                  onClick={(e) => {
                    e.preventDefault();
                    if (pagination.has_more) setPage(p => p + 1);
                  }}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
}
